import React from 'react';
import styled from 'styled-components';
import { FieldLabel } from './FieldSettingsModal';
import { CheckboxItem } from '../../listing/ColumnIdentifier';
import CustomInput from '../../form/CustomInput';
import { useTheme } from '../../../middleware/ThemeContext';

const Container = styled.div`
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SizeOptions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;
`;

const SizeOption = styled.div<{ active: boolean }>`
  padding: 8px 16px;
  border: 1px solid ${({ active }) => (active ? '#3D7EFF' : '#ddd')};
  border-radius: 4px;
  cursor: pointer;
  background: ${({ active }) => (active ? '#F0F7FF' : 'white')};
  color: ${({ active }) => (active ? '#3D7EFF' : '#333')};
  font-weight: ${({ active }) => (active ? '600' : 'normal')};
  flex: 1;
  text-align: center;
`;

const RangeInputGroup = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const CheckboxGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
`;

interface SliderSettingsProps {
    fieldSize: string;
    setFieldSize: (val: string) => void;
    initialValue: string;
    setInitialValue: (val: string) => void;
    minValue: string;
    setMinValue: (val: string) => void;
    maxValue: string;
    setMaxValue: (val: string) => void;
    unit: string;
    setUnit: (val: string) => void;
    stepValue: string;
    setStepValue: (val: string) => void;
    showStepValues: boolean;
    setShowStepValues: (val: boolean) => void;
    showStepPointers: boolean;
    setShowStepPointers: (val: boolean) => void;
    showMinMaxLabel: boolean;
    setShowMinMaxLabel: (val: boolean) => void;
}

const SliderSettings: React.FC<SliderSettingsProps> = ({
    fieldSize,
    setFieldSize,
    initialValue,
    setInitialValue,
    minValue,
    setMinValue,
    maxValue,
    setMaxValue,
    unit,
    setUnit,
    stepValue,
    setStepValue,
    showStepValues,
    setShowStepValues,
    showStepPointers,
    setShowStepPointers,
    showMinMaxLabel,
    setShowMinMaxLabel,
}) => {
    const { theme } = useTheme();
    const sizeOptions = ['Small', 'Medium', 'Large'];

    return (
        <Container>
            <div>
                <FieldLabel theme={theme}>Field Size</FieldLabel>
                <SizeOptions>
                    {sizeOptions.map((size) => (
                        <SizeOption
                            key={size}
                            active={fieldSize === size}
                            onClick={() => setFieldSize(size)}
                        >
                            {size}
                        </SizeOption>
                    ))}
                </SizeOptions>
            </div>

            <div>
                <FieldLabel theme={theme}>Initial Value</FieldLabel>
                <CustomInput
                    type="number"
                    placeholder="Initial Value"
                    value={initialValue}
                    onChange={setInitialValue}
                />

                <FieldLabel theme={theme}>Range Restriction</FieldLabel>
                <RangeInputGroup>
                    <CustomInput
                        type="number"
                        placeholder="Min"
                        value={minValue}
                        onChange={setMinValue}
                    />
                    <span>|</span>
                    <CustomInput
                        type="number"
                        placeholder="Max"
                        value={maxValue}
                        onChange={setMaxValue}
                    />
                </RangeInputGroup>
            </div>
            <div>
                <FieldLabel theme={theme}>Unit</FieldLabel>
                <CustomInput
                    type="text"
                    placeholder="Unit (e.g., kg, m)"
                    value={unit}
                    onChange={setUnit}
                />
            </div>
            <div>
                <FieldLabel theme={theme}>Step Value</FieldLabel>
                <CustomInput
                    type="number"
                    placeholder="Step Value"
                    value={stepValue}
                    onChange={setStepValue}
                />

                <span style={{ fontSize: 12, color: `${theme.CorporateTheme.colors.textSecondary}`, display: 'block', marginBottom: '8px' }}>
                    Note: Step indicators will be displayed when the number of steps is 10 or less.
                </span>

                <CheckboxGrid>
                    <CheckboxItem theme={theme}>
                        <input
                            type="checkbox"
                            checked={showStepValues}
                            onChange={() => setShowStepValues(!showStepValues)}
                        />
                        Show Step Values
                    </CheckboxItem>
                    <CheckboxItem theme={theme}>
                        <input
                            type="checkbox"
                            checked={showStepPointers}
                            onChange={() => setShowStepPointers(!showStepPointers)}
                        />
                        Show Step Pointers
                    </CheckboxItem>
                    <CheckboxItem theme={theme}>
                        <input
                            type="checkbox"
                            checked={showMinMaxLabel}
                            onChange={() => setShowMinMaxLabel(!showMinMaxLabel)}
                        />
                        Show Min/Max Label
                    </CheckboxItem>
                </CheckboxGrid>
            </div>
        </Container>
    );
};

export default SliderSettings;